import { Icon, type IconName } from './icon/index.ts';
import styles from './Input.module.css';

/**
 * Design system § 14 — Input.
 *
 * Uncontrolled-optional, unlike the components of the practice loop: a text
 * field's value is the browser's to hold until a form is submitted, and there
 * is no server-confirmed outcome it could misrepresent. Pass `value` to
 * control it, `defaultValue` not to.
 *
 * The label lives in Field, which is where § 14 puts it.
 */

interface InputProps {
  id: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  type?: 'text' | 'email' | 'number' | 'search';
  placeholder?: string;
  disabled?: boolean;
  /** Set when Field is rendering an error for this control. */
  invalid?: boolean;
  required?: boolean;
  describedBy?: string;
  /**
   * § 16 — `compact` is the toolbar and table-row height, for a control that
   * is one of a dozen on a row rather than the screen's own decision.
   */
  size?: 'default' | 'compact';
  /**
   * A leading glyph, decorative — it names the field's KIND, never its state
   * (§ 12), and the label above the field still says what it is. The one
   * caller today is a toolbar's search field, where the magnifier is what
   * makes a search box recognisable as one at a glance.
   */
  icon?: IconName;
  /** The accessible name, where no visible `Field` label supplies one. */
  ariaLabel?: string;
}

export function Input({
  id,
  value,
  defaultValue,
  onChange,
  type = 'text',
  placeholder,
  disabled = false,
  invalid = false,
  required,
  describedBy,
  size = 'default',
  icon,
  ariaLabel,
}: InputProps) {
  const field = (
    <input
      id={id}
      className={styles.input}
      type={type}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange ? (event) => onChange(event.target.value) : undefined}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy ?? (invalid ? `${id}-error` : undefined)}
      data-state={disabled ? 'disabled' : invalid ? 'error' : 'default'}
      data-size={size}
      data-adorned={icon ? 'true' : undefined}
    />
  );

  if (!icon) return field;

  return (
    <span className={styles.wrapper} data-size={size}>
      <span className={styles.adornment} aria-hidden="true">
        <Icon name={icon} size={size === 'compact' ? 16 : 20} />
      </span>
      {field}
    </span>
  );
}
